<script setup>
/* global defineProps, defineEmits */
import { computed } from "vue";
import { useUiStore } from "@/app/stores/uiStore";

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  pageSize: {
    type: Number,
    required: true,
  },
  total: {
    type: Number,
    required: true,
  },
  pageSizeOptions: {
    type: Array,
    default: () => [10, 20, 30, 50, 100, 200],
  },
  size: {
    type: String,
    default: "large",
  },
  layout: {
    type: String,
    default: "prev, pager, next, sizes",
  },
  showAlways: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  pagerCount: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits([
  "update:currentPage",
  "update:pageSize",
  "current-change",
  "size-change",
  "change",
]);

const uiStore = useUiStore();

const isDisabled = computed(() => props.disabled || uiStore.loading);

const normalizePageSizeOptions = (options) => {
  return options
    .filter((option) => Number.isFinite(option) && option > 0)
    .sort((firstOption, secondOption) => firstOption - secondOption)
    .filter((option, index, options) => options.indexOf(option) === index);
};

const configuredPageSizeOptions = computed(() => {
  return normalizePageSizeOptions(props.pageSizeOptions);
});

const normalizedPageSizeOptions = computed(() => {
  return normalizePageSizeOptions([...props.pageSizeOptions, props.pageSize]);
});

const availablePageSizeOptions = computed(() => {
  const options = configuredPageSizeOptions.value;
  const nextPageSize = options.find((option) => option > props.total);
  const availableOptions = options.filter((option) => option <= props.total);

  if (nextPageSize) {
    availableOptions.push(nextPageSize);
  }

  if (Number.isFinite(props.pageSize) && props.pageSize > 0) {
    availableOptions.push(props.pageSize);
  }

  return normalizePageSizeOptions(
    availableOptions.length > 0 ? availableOptions : options.slice(0, 1)
  );
});

const minimumPageSize = computed(() => normalizedPageSizeOptions.value[0] || props.pageSize);

const resolvedPagerCount = computed(() => {
  if (!props.pagerCount) {
    return undefined;
  }

  const pagerCount = Math.max(5, Math.min(props.pagerCount, 21));
  return pagerCount % 2 === 0 ? pagerCount - 1 : pagerCount;
});

const showPagination = computed(() => {
  return props.showAlways || props.total > minimumPageSize.value;
});

const updateCurrentPage = (currentPage) => {
  if (isDisabled.value) {
    return;
  }

  emit("update:currentPage", currentPage);
};

const updatePageSize = (pageSize) => {
  if (isDisabled.value) {
    return;
  }

  emit("update:pageSize", pageSize);
};

const onCurrentChange = (currentPage) => {
  if (isDisabled.value) {
    return;
  }

  emit("current-change", currentPage);
};

const onSizeChange = (pageSize) => {
  if (isDisabled.value) {
    return;
  }

  emit("size-change", pageSize);
};

const onChange = (currentPage, pageSize) => {
  if (isDisabled.value) {
    return;
  }

  emit("change", currentPage, pageSize);
};
</script>

<template>
  <el-pagination
    v-if="showPagination"
    class="tracker-pagination"
    :current-page="currentPage"
    :page-size="pageSize"
    :size="size"
    :page-sizes="availablePageSizeOptions"
    :pager-count="resolvedPagerCount"
    :layout="layout"
    :total="total"
    :disabled="isDisabled"
    @update:current-page="updateCurrentPage"
    @update:page-size="updatePageSize"
    @current-change="onCurrentChange"
    @size-change="onSizeChange"
    @change="onChange"
  />
</template>
